import type { ReturnFilters } from "../api/use-returns";

/**
 * Lets a key be passed as `undefined` (to clear it) without violating
 * `exactOptionalPropertyTypes`. Used at the filter-change callback boundary —
 * mirrors `FilterUpdate` in order-url-state.ts.
 */
export type ReturnFilterUpdate = { [K in keyof ReturnFilters]?: ReturnFilters[K] | undefined };

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** validateSearch body for /returns. Invalid values degrade to "no filter". */
export function parseReturnSearch(raw: Record<string, unknown>): ReturnFilters {
  const str = (v: unknown): string =>
    typeof v === "string" ? v : typeof v === "number" || typeof v === "boolean" ? String(v) : "";
  const out: ReturnFilters = {};
  const marketplace = str(raw.marketplace).trim();
  if (marketplace) out.marketplace = marketplace;
  const search = str(raw.search).trim();
  if (search) out.search = search;
  const df = str(raw.date_from).trim();
  if (YMD.test(df)) out.date_from = df;
  const dt = str(raw.date_to).trim();
  if (YMD.test(dt)) out.date_to = dt;
  const offset = Number(raw.offset);
  if (Number.isInteger(offset) && offset >= 0) out.offset = offset;
  const limit = Number(raw.limit);
  if (Number.isInteger(limit) && limit > 0) out.limit = limit;
  return out;
}

/** True when at least one non-paging filter is active — drives Clear-button visibility. */
export function hasActiveReturnFilters(f: ReturnFilters): boolean {
  return Boolean(f.marketplace || f.search || f.date_from || f.date_to);
}

/** Returns the filter with offset cleared — call when any non-paging filter changes
 * so results jump back to page 1. */
export function resetReturnPage(f: ReturnFilters): ReturnFilters {
  return { ...f, offset: 0 };
}

/**
 * Produces the next filter from the current one plus a set of updates.
 * A key whose update value is `undefined` is removed (clears that filter).
 */
export function mergeReturnFilter(
  current: ReturnFilters,
  updates: ReturnFilterUpdate,
): ReturnFilters {
  const next: ReturnFilters = { ...current };
  for (const k of Object.keys(updates) as (keyof ReturnFilterUpdate)[]) {
    if (updates[k] === undefined) delete next[k];
    else (next as Record<string, unknown>)[k] = updates[k];
  }
  return next;
}
